import React, { useState, useEffect } from 'react';
import { useToast } from '../contexts/ToastContext';

function AddCategoryModal({ isOpen, onClose, onSubmit, loading: submitLoading, category: editCategory, isEditMode = false, mainCategories = [] }) {
  const { showError } = useToast();
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image: null, // File object for new uploads
    is_active: true,
    parent_id: null, // Parent category ID (null for main category)
  });

  const [existingImage, setExistingImage] = useState(null); // URL string for existing image
  const [imagePreview, setImagePreview] = useState(null);

  // Reset form when modal opens/closes or edit category changes
  useEffect(() => {
    if (isOpen) {
      if (isEditMode && editCategory) {
        // Populate form with existing category data for editing
        setFormData({
          name: editCategory.name || '',
          description: editCategory.description || '',
          image: null, // New file uploads only
          is_active: editCategory.is_active !== false && editCategory.is_active !== null,
          parent_id: editCategory.parent_id || null,
        });
        
        // Set existing image URL
        setExistingImage(editCategory.image || null);
        setImagePreview(editCategory.image || null);
      } else {
        // Reset form for new category
        setFormData({
          name: '',
          description: '',
          image: null,
          is_active: true,
          parent_id: null, // Default to main category
        });
        setExistingImage(null);
        setImagePreview(null);
      }
    }
  }, [isOpen, isEditMode, editCategory]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        showError('Please select a valid image file');
        return;
      }
      
      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        showError('Image size must be less than 2MB');
        return;
      }

      setFormData(prev => ({ ...prev, image: file }));
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, image: null }));
    setImagePreview(null);
    setExistingImage(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.name.trim()) {
      showError('Category name is required');
      return;
    }

    // Trim name before submitting
    const submitData = {
      ...formData,
      name: formData.name.trim(),
      description: formData.description ? formData.description.trim() : '',
    };

    // Debug: Log what we're submitting
    console.log('🔍 AddCategoryModal - formData:', formData);
    console.log('🔍 AddCategoryModal - submitData:', submitData);
    console.log('🔍 AddCategoryModal - submitData.name:', submitData.name);
    console.log('🔍 AddCategoryModal - submitData.name type:', typeof submitData.name);

    try {
      await onSubmit(submitData);
      // Reset form after successful submission
      setFormData({
        name: '',
        description: '',
        image: null,
        is_active: true,
        parent_id: null,
      });
      setExistingImage(null);
      setImagePreview(null);
    } catch (error) {
      // Error handling is done in parent component
      throw error;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-8 animate-modal-pop">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary via-primary to-primary-dark px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">
                {isEditMode ? 'Edit Category' : 'Add New Category'}
              </h2>
              <p className="text-sm text-white/80 mt-1">
                {isEditMode ? 'Update category information' : 'Create a new product category'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={submitLoading}
            className="p-2 hover:bg-white/20 rounded-xl transition-colors disabled:opacity-50"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-8">
          <div className="space-y-6">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                Category Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 text-sm font-medium"
                placeholder="e.g., Electronics, Clothing, Home & Garden"
              />
            </div>

            {/* Parent Category */}
            <div>
              <label htmlFor="parent_id" className="block text-sm font-semibold text-gray-700 mb-2">
                Parent Category
              </label>
              <select
                id="parent_id"
                name="parent_id"
                value={formData.parent_id || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    parent_id: value === '' ? null : Number(value)
                  }));
                }}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 text-sm font-medium"
              >
                <option value="">Main Category (No Parent)</option>
                {mainCategories
                  .filter(cat => !isEditMode || cat.id !== editCategory?.id) // Exclude current category when editing
                  .map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {formData.parent_id ? 'This will be a subcategory' : 'This will be a main category'}
              </p>
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-semibold text-gray-700 mb-2">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 text-sm font-medium resize-none"
                placeholder="Brief description of this category..."
              />
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Category Image
              </label>
              <div className="space-y-4">
                {/* Image Preview */}
                {(imagePreview || existingImage) && (
                  <div className="relative inline-block">
                    <img
                      src={imagePreview || existingImage}
                      alt="Category preview"
                      className="w-32 h-32 object-cover rounded-xl border-2 border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}

                {/* File Input */}
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-primary hover:bg-primary/5 transition-all duration-200">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <svg className="w-10 h-10 mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <p className="mb-2 text-sm text-gray-500">
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-gray-500">PNG, JPG, GIF or WEBP (MAX. 2MB)</p>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </label>
              </div>
            </div>

            {/* Active Status */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is_active"
                name="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
                className="w-5 h-5 text-primary border-gray-300 rounded focus:ring-2 focus:ring-primary/20 cursor-pointer"
              />
              <label htmlFor="is_active" className="text-sm font-semibold text-gray-700 cursor-pointer">
                Category is active (visible to customers)
              </label>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={submitLoading}
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-all duration-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitLoading}
              className="px-6 py-3 bg-gradient-to-r from-primary via-primary to-primary-dark text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-200 disabled:opacity-50 flex items-center gap-2"
            >
              {submitLoading ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>{isEditMode ? 'Updating...' : 'Creating...'}</span>
                </>
              ) : (
                <span>{isEditMode ? 'Update Category' : 'Create Category'}</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddCategoryModal;

